/**
 * SubFlow Node Component (New - BaseNode Implementation)
 * Node that displays a mini workflow inside it
 */

import React from 'react';
import { NodeProps } from 'reactflow';
import { WorkflowNode } from '@flow-studio/types';
import { BaseNodeClass, withNodeHooks, NodeConfig } from './Base/BaseNode';
import { useUIStore } from '@flow-studio/store';
import { PackageIcon, ChevronDownIcon, ClockIcon } from '@bizfirst/common-react';

const PinIcon = ({ size = 11, color = 'white' }: { size?: number; color?: string }) => (
  <svg xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
    <path d="M12 17v5"/>
    <path d="M9 10.76a2 2 0 0 1-1.11 1.79l-1.78.9A2 2 0 0 0 5 15.24V17h14v-1.76a2 2 0 0 0-1.11-1.79l-1.78-.9A2 2 0 0 1 15 10.76V6h1a2 2 0 0 0 0-4H8a2 2 0 0 0 0 4h1v4.76z"/>
  </svg>
);

/**
 * SubFlowNode Class extending BaseNode
 */
class SubFlowNodeClass extends BaseNodeClass {
  protected config(): NodeConfig {
    return {
      ...super.config(),
      showToolbarOnClick: true,
      showToolbarOnHover: false,
      customClassName: 'subflow-node',
    };
  }

  protected getClassName(): string {
    const { selected, data } = this.props;
    return `subflow-node ${selected ? 'selected' : ''} ${
      data.isDisabled ? 'disabled' : ''
    }`;
  }

  protected getContainerStyle(): React.CSSProperties {
    const { data } = this.props;
    return {
      opacity: data.isDisabled ? 0.6 : 1,
    };
  }

  protected renderContent(): React.ReactNode {
    const { data } = this.props;

    // Mock mini workflow visualization
    const miniNodes = [
      { icon: '', type: 'start', label: 'Start' },
      { icon: '↻', type: 'process', label: 'Process' },
      { icon: '✓', type: 'end', label: 'End' },
    ];

    const subflowInfo = data.parameters?.subflowInfo || {
      nodeCount: 5,
      executionTime: '2.3s',
      lastRun: 'Today',
    };

    return (
      <>
        {/* Header */}
        <div className="subflow-header bf-node-header">
          <span className="subflow-header-icon"><PackageIcon size={16} /></span>
          <span
            className="bf-node-header-title"
            title={data.displayName || 'SubFlow'}
            style={{ flex: 1, minWidth: 0, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
          >
            {data.displayName || 'SubFlow'}
          </span>
          {data.processElement?.pinnedData && (
            <span title="Output pinned" style={{ display: 'inline-flex', alignItems: 'center', marginLeft: 6, opacity: 0.85 }}>
              <PinIcon size={11} color="white" />
            </span>
          )}
          <span className="subflow-header-collapse"><ChevronDownIcon size={16} /></span>
        </div>

        {/* Body - Mini Workflow Visualization */}
        <div className="subflow-body bf-node-body nodrag">
          {data.description && (
            <div className="subflow-description">{data.description}</div>
          )}

          {/* Mini Workflow */}
          <div className="subflow-mini-workflow">
            {miniNodes.map((node, index) => (
              <React.Fragment key={index}>
                <div
                  className={`subflow-mini-node ${node.type}`}
                  title={node.label}
                >
                  {node.icon}
                </div>
                {index < miniNodes.length - 1 && (
                  <span className="subflow-mini-arrow">→</span>
                )}
              </React.Fragment>
            ))}
          </div>

          {/* Open SubFlow Button */}
          <button
            className="subflow-open-button nodrag nopan"
            onClick={(e) => {
              e.stopPropagation();
              this.handleOpenSubflow();
            }}
          >
            Open ↗
          </button>
        </div>

        {/* Footer - Stats */}
        <div className="subflow-footer">
          <span className="subflow-stats">{subflowInfo.nodeCount} nodes</span>
          <span className="subflow-badge"><ClockIcon size={12} /> {subflowInfo.executionTime}</span>
        </div>
      </>
    );
  }

  /**
   * Handle opening subflow
   */
  private handleOpenSubflow = (): void => {
    const { data } = this.props;
    useUIStore.getState().showToast(
      'info',
      `Opening subflow: ${data.displayName} (ID: ${data.subWorkflowId || 'not set'})`
    );
  };
}

/**
 * Export with hooks injected
 */
export const SubFlowNode: React.FC<NodeProps<WorkflowNode['data']>> =
  withNodeHooks(SubFlowNodeClass);
